import { createContext, useContext, useMemo } from "react";
import { WORK_MODULES } from "@/modules/workModuleCore";
import { isConfiguracionHubPath } from "@/modules/config/configuracionRoutes";
import { isEquipoBasePath } from "@/modules/equipo/equipoRoutes";

/** Ámbitos de cliente de trabajo (localStorage independiente por módulo). */
export const CLIENT_MODULE_SCOPES = {
  VISOR_SAT: "visor_sat",
  ASISTENTE_LABORAL: "asistente_laboral",
  TRAMITES: "tramites",
};

const LEGACY_STORAGE_KEY = "vf:selected_client_id";

export function storageKeyForScope(scope) {
  return `vf:selected_client_id:${scope || CLIENT_MODULE_SCOPES.VISOR_SAT}`;
}

/**
 * Resuelve el ámbito de cliente según ruta y módulo activo.
 * Trámites Fiscales usa contexto propio aunque comparta sidebar del Visor.
 */
export function resolveClientScope(pathname, workModuleId) {
  const path = (pathname || "").split("?")[0];
  if (
    path === "/tramites-fiscales"
    || path.startsWith("/tramites-fiscales/")
    || path === "/tramites/inicio"
    || path.startsWith("/tramites/")
  ) {
    return CLIENT_MODULE_SCOPES.TRAMITES;
  }
  if (workModuleId === WORK_MODULES.ASISTENTE_LABORAL) {
    return CLIENT_MODULE_SCOPES.ASISTENTE_LABORAL;
  }
  return CLIENT_MODULE_SCOPES.VISOR_SAT;
}

/** Rutas del hub / administración sin selector de cliente operativo. */
export function isHubOrAdminPath(pathname) {
  const path = (pathname || "").split("?")[0];
  if (path === "/" || path === "") return true;
  if (isEquipoBasePath(path)) return true;
  if (isConfiguracionHubPath(path)) return true;
  const hubOnly = new Set([
    "/registro-inicial",
    "/recuperacion-acceso",
    "/recuperacion-extravio",
    "/ayuda",
    "/catalogo-compradores",
    "/cartera-licencias",
    "/usuarios-distribuidor",
    "/disenador/inicio",
    "/disenador/herramientas",
    "/disenador/licencias",
    "/disenador/modulos-comprador",
    "/disenador/centro-bnt",
    "/datos-maestros",
    "/gestion-datos",
    "/exportacion-local",
    "/gestionar-xml",
  ]);
  return hubOnly.has(path);
}

const ClientModuleScopeContext = createContext({
  scope: CLIENT_MODULE_SCOPES.VISOR_SAT,
});

export function ClientModuleScopeProvider({ scope, children }) {
  const value = useMemo(
    () => ({ scope: scope || CLIENT_MODULE_SCOPES.VISOR_SAT }),
    [scope],
  );
  return (
    <ClientModuleScopeContext.Provider value={value}>
      {children}
    </ClientModuleScopeContext.Provider>
  );
}

export function useClientModuleScope() {
  return useContext(ClientModuleScopeContext).scope;
}

/** Migra una vez el id global legacy al ámbito visor_sat. */
export function migrateLegacyClientId(scope) {
  if (scope !== CLIENT_MODULE_SCOPES.VISOR_SAT) return;
  try {
    const legacy = localStorage.getItem(LEGACY_STORAGE_KEY);
    const scopedKey = storageKeyForScope(scope);
    if (legacy && !localStorage.getItem(scopedKey)) {
      localStorage.setItem(scopedKey, legacy);
    }
  } catch {
    /* ignore */
  }
}
